import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { FONTS, THEMES, resolveSkin, loadFont } from "@/lib/skin";
import { Choice, LivePreview } from "@/pages/admin/AdminSkin";

const COLORS = ["primary_color", "accent_color"];

export default function TdvSkin() {
  const [b, setB] = useState(null);
  const [tours] = useList("/admin/tdv/tours");
  const [target, setTarget] = useState("");
  useEffect(() => { api.get("/tdv/account").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  Object.keys(FONTS).forEach(loadFont);
  if (!b) return null;
  const s = resolveSkin(b, null);
  const set = (k, v) => (COLORS.includes(k) ? setB({ ...b, [k]: v }) : setB({ ...b, skin: { ...(b.skin || {}), [k]: v } }));
  const theme = (key) => {
    const { name, ...t } = THEMES[key];
    const rest = Object.fromEntries(Object.entries(t).filter(([k]) => !COLORS.includes(k)));
    setB({ ...b, primary_color: t.primary_color, accent_color: t.accent_color, skin: { ...(b.skin || {}), ...rest, theme: key } });
    toast(`${name} applied. Tweak anything, then save.`);
  };
  const save = async () => {
    try { const { data } = await api.put("/tdv/skin", { primary_color: b.primary_color, accent_color: b.accent_color, skin: b.skin || {} }); setB(data); toast.success("Skin saved for all your tours"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const ready = (tours || []).filter((t) => t.status === "ready");
  const first = ready.find((t) => t.id === target) || ready[0];
  return (
    <div data-testid="tdv-skin">
      <PageTitle title="Tour skin" sub="Pick a theme, then fine-tune fonts, colours, panels and the top bar. The preview is your real tour and updates as you go." right={<div className="flex gap-2">{first && <a href={`/3d/${first.id}`} target="_blank" rel="noreferrer"><AdminButton variant="outline" data-testid="tdv-skin-preview"><ExternalLink className="w-4 h-4" />Open a tour</AdminButton></a>}<AdminButton onClick={save} data-testid="tdv-skin-save"><Check className="w-4 h-4" />Save skin</AdminButton></div>} />
      <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6 items-start">
      <div className="rounded-3xl bg-white border p-6 space-y-6">
        <div>
          <p className="text-sm font-semibold mb-2">Themes</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(THEMES).map(([k, t]) => (
              <button key={k} onClick={() => theme(k)} className={`rounded-2xl border p-3 text-left transition-colors hover:border-slate-500 ${s.theme === k ? "ring-2 ring-slate-900" : ""}`} data-testid={`tdv-skin-theme-${k}`}>
                <span className="flex gap-1 mb-2"><span className="w-6 h-6 rounded-full border" style={{ background: t.primary_color }} /><span className="w-6 h-6 rounded-full border" style={{ background: t.accent_color }} /></span>
                <span className="text-sm font-semibold" style={{ fontFamily: `"${FONTS[t.font][1]}"` }}>{t.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[["primary_color", "Main"], ["accent_color", "Accent"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-500">{l}<input type="color" value={s[k] || "#0F2A47"} onChange={(e) => set(k, e.target.value)} className="mt-1 block w-full h-10 rounded-lg border" data-testid={`tdv-skin-${k}`} /></label>
          ))}
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Fonts</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(FONTS).map(([k, [name, head]]) => (
              <button key={k} onClick={() => set("font", k)} className={`rounded-xl border px-3 py-2 text-left ${(s.font || "classic") === k ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:border-slate-400"}`} data-testid={`tdv-skin-font-${k}`}><span className="block text-base" style={{ fontFamily: `"${head}"` }}>{name}</span></button>
            ))}
          </div>
        </div>
        <Choice k="corners" label="Buttons and panels" hint="Corner style" s={s} set={set} />
        <Choice k="panel" label="Panel style" s={s} set={set} />
        <Choice k="tone" label="Panel colour" s={s} set={set} />
        {s.tone === "color" && <label className="text-xs text-slate-500 block">Panel colour<input type="color" value={s.panel_color || "#1E3A5F"} onChange={(e) => set("panel_color", e.target.value)} className="mt-1 block w-40 h-10 rounded-lg border" data-testid="tdv-skin-panel_color" /></label>}
        <Choice k="bar_style" label="Top bar" s={s} set={set} />
      </div>
      <div className="space-y-3">
        {ready.length > 1 && <select value={first?.id || ""} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-xl border px-3 text-sm bg-white" data-testid="tdv-skin-preview-tour">{ready.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}
        <LivePreview houseId={first?.id} skin={s} t={{ item: "tour", homes: "Tours" }} src={first && `/3d/${first.id}?skin_preview=1`} note="This is your real 3DVista tour. Changes show here before you save." />
      </div>
      </div>
    </div>
  );
}
